import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { AdminSubscriptionResetResponse } from '@utils/types/admin';
import { ADMIN_ORG_SUBSCRIPTION_QUERY_KEY } from './use-get-admin-organization-subscription';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * Hook to reset an organization's billing period usage
 *
 * POST /api/admin/on-prem/organizations/{id}/subscription/reset-usage
 *
 * Resets usage counters and starts a new billing period
 */
export const useResetAdminOrganizationSubscription = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const resetUsage = async (organizationId: string) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/subscription/reset-usage`;
    const response = await Axios.post<AdminSubscriptionResetResponse>(url);
    return response.data;
  };

  return useMutation<
    AdminSubscriptionResetResponse,
    AxiosError<BaseError>,
    string
  >(resetUsage, {
    onSuccess: (_, organizationId) => {
      toast.success(t('usage-reset-successfully'));
      queryClient.invalidateQueries([
        ADMIN_ORG_SUBSCRIPTION_QUERY_KEY,
        organizationId
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-reset-usage');
      toast.error(errorMessage);
    }
  });
};
